import React, { useState, useEffect } from 'react';
import { Lock, Filter, Download, Search, BarChart3, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DHReport — Super-Admin only.
 * Password-gated panel that aggregates Work / App / Device hours per staff
 * over a date range, filterable by geo-fence, department, designation and name.
 */
export default function DHReport() {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [busy, setBusy] = useState(false);

  const today = new Date();
  const fmtDate = (d) => d.toISOString().slice(0, 10);
  const monthAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);

  const [filters, setFilters] = useState({
    start_date: fmtDate(monthAgo),
    end_date: fmtDate(today),
    geo_fence_location_id: '',
    department_id: '',
    designation_id: '',
    name: '',
  });
  const [opts, setOpts] = useState({ geofences: [], departments: [], designations: [] });
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ count: 0, days: 0 });
  const [loaded, setLoaded] = useState(false);

  // ===== Password unlock =====
  const handleUnlock = async (e) => {
    e?.preventDefault();
    setPwError('');
    if (!password.trim()) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `${API_URL}/api/super-admin/dh-report/options?password=${encodeURIComponent(password)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setPwError(d.detail || 'Invalid password');
        return;
      }
      const data = await r.json();
      setOpts({
        geofences: data.geofences || [],
        departments: data.departments || [],
        designations: data.designations || [],
      });
      setUnlocked(true);
    } catch {
      setPwError('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // ===== Run report =====
  const fetchReport = async () => {
    if (!unlocked) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/super-admin/dh-report`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          start_date: filters.start_date,
          end_date: filters.end_date,
          geo_fence_location_id: filters.geo_fence_location_id || null,
          department_id: filters.department_id || null,
          designation_id: filters.designation_id || null,
          name: filters.name?.trim() || null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d.detail || `Report failed (${r.status})`);
        return;
      }
      const data = await r.json();
      setRows(data.rows || []);
      setMeta({ count: data.count || 0, days: data.days || 0 });
      setLoaded(true);
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Auto-run once unlocked with default filters
  useEffect(() => {
    if (unlocked && !loaded) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  // ===== Excel export (client-side CSV → .xlsx-friendly) =====
  const exportCsv = () => {
    if (!rows.length) return;
    const head = ['Staff Name', 'Emp ID', 'Mobile #', 'Department', 'Designation', 'Location', 'Total Work Hours', 'Total App Hours', 'Total Device Hours'];
    const body = rows.map(r => [
      r.name, r.emp_id, r.mobile, r.department, r.designation, r.location,
      r.work_hours, r.app_hours, r.device_hours
    ]);
    const csv = [head, ...body].map(row => row.map(cell => {
      const s = String(cell ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DH_Report_${filters.start_date}_to_${filters.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ===== UI =====
  if (!unlocked) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="dh-locked">
        <form
          onSubmit={handleUnlock}
          className="bg-white rounded-2xl shadow-xl border border-purple-100 w-full max-w-md p-6"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-100">
              <ShieldAlert size={22} className="text-purple-700" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">DH Report — Restricted</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Super-Admin only. Enter the report password to unlock the Device-Hours dashboard.
              </p>
            </div>
          </div>

          <label className="block text-xs text-gray-600 mb-1">Password</label>
          <div className="relative mb-3">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="DH report password"
              data-testid="dh-password-input"
            />
          </div>

          {pwError && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3" data-testid="dh-password-error">
              {pwError}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !password.trim()}
            className="w-full py-2 text-sm font-semibold rounded-lg bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-60"
            data-testid="dh-unlock-btn"
          >
            {busy ? 'Verifying…' : 'Unlock DH Report'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="dh-report">
      <div className="flex items-start justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 size={22} className="text-purple-600" /> DH Report
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Total Work / App / Device hours across the selected range. Device hours are computed
            between check-in and check-out, excluding break and permission time.
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={!rows.length}
          className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm disabled:opacity-50"
          data-testid="dh-export-btn"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 mb-3 grid grid-cols-2 md:grid-cols-7 gap-2" data-testid="dh-filters">
        <div className="col-span-1">
          <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">From</label>
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
            data-testid="dh-start-date"
          />
        </div>
        <div className="col-span-1">
          <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">To</label>
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
            data-testid="dh-end-date"
          />
        </div>
        <div className="col-span-1">
          <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Geo-fence</label>
          <select
            value={filters.geo_fence_location_id}
            onChange={(e) => setFilters({ ...filters, geo_fence_location_id: e.target.value })}
            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs bg-white"
            data-testid="dh-filter-geo"
          >
            <option value="">All</option>
            {opts.geofences.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </div>
        <div className="col-span-1">
          <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Department</label>
          <select
            value={filters.department_id}
            onChange={(e) => setFilters({ ...filters, department_id: e.target.value })}
            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs bg-white"
            data-testid="dh-filter-dept"
          >
            <option value="">All</option>
            {opts.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div className="col-span-1">
          <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Designation</label>
          <select
            value={filters.designation_id}
            onChange={(e) => setFilters({ ...filters, designation_id: e.target.value })}
            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs bg-white"
            data-testid="dh-filter-desig"
          >
            <option value="">All</option>
            {opts.designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Search name / ID / mobile</label>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
              placeholder="contains…"
              className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-lg text-xs"
              data-testid="dh-filter-name"
            />
          </div>
        </div>
        <div className="col-span-2 md:col-span-7 flex items-center gap-2 mt-1">
          <button
            onClick={fetchReport}
            disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50"
            data-testid="dh-apply-btn"
          >
            <Filter size={12} /> {busy ? 'Loading…' : 'Apply Filters'}
          </button>
          <span className="text-xs text-gray-500" data-testid="dh-summary">
            {meta.count} staff · {meta.days} day{meta.days === 1 ? '' : 's'} in range
          </span>
        </div>
      </div>

      {/* Results table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="dh-results">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <Th>#</Th>
                <Th>Staff Name</Th>
                <Th>Emp ID</Th>
                <Th>Mobile #</Th>
                <Th className="text-right">Total Work Hours</Th>
                <Th className="text-right">Total App Hours</Th>
                <Th className="text-right">Total Device Hours</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500" data-testid="dh-empty">
                    {busy ? 'Loading…' : 'No matching staff in the selected range.'}
                  </td>
                </tr>
              )}
              {rows.map((r, i) => (
                <tr key={r.staff_id} className="hover:bg-gray-50" data-testid={`dh-row-${r.staff_id}`}>
                  <td className="px-4 py-2 text-gray-500 text-xs">{i + 1}</td>
                  <td className="px-4 py-2 font-medium text-gray-900">{r.name}</td>
                  <td className="px-4 py-2 text-gray-700">{r.emp_id || '—'}</td>
                  <td className="px-4 py-2 text-gray-700">{r.mobile}</td>
                  <td className="px-4 py-2 text-right font-semibold text-blue-700">{r.work_hours}h</td>
                  <td className="px-4 py-2 text-right font-semibold text-indigo-700">{r.app_hours}h</td>
                  <td className="px-4 py-2 text-right font-semibold text-emerald-700">{r.device_hours}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Th({ children, className = '' }) {
  return (
    <th className={`px-4 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide ${className}`}>
      {children}
    </th>
  );
}
